/**
 * Tour-request submissions — used by the shared "Request edits / order"
 * modal behind every Order CAD File / Propose Layout Change / Report an
 * Issue / Request Different Style / Request New Interior Design / Request
 * a Video button.
 *
 * Backed by the standalone `tourRequest` Cloud Function (gen2, id3d-prod),
 * which authenticates the caller via Firebase ID token, stores any
 * uploaded files in GCS, and emails michael@the3dapp.com.
 */

import { getAuth } from 'firebase/auth';

export interface UploadAttachment {
  kind: 'upload';
  filename: string;
  contentType: string;
  dataUrl: string;
}

export interface GalleryAttachment {
  kind: 'gallery';
  url: string;
}

export type TourRequestAttachment = UploadAttachment | GalleryAttachment;

export interface TourRequestPayload {
  tourId: string;
  tourName: string;
  requestType: string;
  message: string;
  attachments: TourRequestAttachment[];
}

interface TourRequestResponse {
  success: boolean;
  requestId: string;
  attachmentUrls: string[];
  emailSent: boolean;
}

export async function sendTourRequest(
  payload: TourRequestPayload,
): Promise<TourRequestResponse> {
  const endpoint = process.env.REACT_APP_REQUEST_API_URL;
  if (!endpoint) {
    throw new Error('REACT_APP_REQUEST_API_URL is not configured');
  }

  const idToken = await getAuth().currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `tour request POST returned ${response.status}`);
  }

  return (await response.json()) as TourRequestResponse;
}
